<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import FullscreenIcon from '@/js/core/components/icons/FullscreenIcon.vue';
import RightCaretIcon from '@/js/core/components/icons/RightCaretIcon.vue';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
});

const rootEl = ref(null);
const viewportEl = ref(null);
const activeIndex = ref(0);
const isFullscreen = ref(false);

const total = computed(() => props.items.length);

// Only the active slide and its neighbors mount their iframes
function isSlideMounted(index) {
  return Math.abs(index - activeIndex.value) <= 1;
}

function goTo(index) {
  const target = Math.max(0, Math.min(total.value - 1, index));
  const viewport = viewportEl.value;

  if (!viewport || target === activeIndex.value) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  viewport.scrollTo({
    left: target * viewport.clientWidth,
    behavior: reducedMotion ? 'auto' : 'smooth',
  });
}

function onScroll() {
  const viewport = viewportEl.value;

  if (!viewport || viewport.clientWidth === 0) return;

  activeIndex.value = Math.max(
    0,
    Math.min(total.value - 1, Math.round(viewport.scrollLeft / viewport.clientWidth))
  );
}

function onKeydown(keyboardEvent) {
  if (keyboardEvent.key === 'ArrowLeft') {
    keyboardEvent.preventDefault();
    goTo(activeIndex.value - 1);
  }
  if (keyboardEvent.key === 'ArrowRight') {
    keyboardEvent.preventDefault();
    goTo(activeIndex.value + 1);
  }
}

async function toggleFullscreen() {
  if (document.fullscreenElement) {
    await document.exitFullscreen();
    return;
  }

  await rootEl.value?.requestFullscreen?.();
}

function onFullscreenChange() {
  isFullscreen.value = document.fullscreenElement === rootEl.value;
}

onMounted(() => {
  document.addEventListener('fullscreenchange', onFullscreenChange);
});

onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', onFullscreenChange);
});
</script>

<template>
  <section
    ref="rootEl"
    aria-label="Video gallery"
    aria-roledescription="carousel"
    class="event-media-gallery"
    :class="{ 'event-media-gallery--fullscreen': isFullscreen }"
    role="group"
    tabindex="0"
    @keydown="onKeydown"
  >
    <div ref="viewportEl" class="event-media-gallery__viewport" @scroll.passive="onScroll">
      <div
        v-for="(item, index) in items"
        :key="item.video_id"
        :aria-label="`Video ${index + 1} of ${total}`"
        aria-roledescription="slide"
        class="event-media-gallery__slide"
        :aria-hidden="index !== activeIndex"
        :inert="index !== activeIndex"
        role="group"
      >
        <iframe
          v-if="isSlideMounted(index)"
          allow="autoplay; fullscreen"
          allowfullscreen
          class="event-media-gallery__frame"
          :src="item.embed_url"
          :title="`Video ${index + 1} of ${total}`"
        ></iframe>
        <div v-else class="event-media-gallery__placeholder"></div>
      </div>
    </div>

    <div class="event-media-gallery__controls">
      <button
        aria-label="Previous video"
        :aria-disabled="activeIndex === 0"
        class="event-media-gallery__arrow event-media-gallery__arrow--prev"
        type="button"
        @click="goTo(activeIndex - 1)"
      >
        <RightCaretIcon />
      </button>

      <span
        :aria-label="`Video ${activeIndex + 1} of ${total}`"
        aria-live="polite"
        class="event-media-gallery__count"
        role="status"
      >
        {{ activeIndex + 1 }} / {{ total }}
      </span>

      <button
        aria-label="Next video"
        :aria-disabled="activeIndex === total - 1"
        class="event-media-gallery__arrow"
        type="button"
        @click="goTo(activeIndex + 1)"
      >
        <RightCaretIcon />
      </button>

      <button
        :aria-label="isFullscreen ? 'Exit fullscreen' : 'View fullscreen'"
        class="event-media-gallery__fullscreen"
        type="button"
        @click="toggleFullscreen"
      >
        <FullscreenIcon />
      </button>
    </div>
  </section>
</template>
